import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { CheckIcon, RefreshCwIcon, UserXIcon } from "lucide-react";
import { planRegistry } from "@/data/meal-plan";
import { useAuth } from "@/context/auth-context";
import { useFriends } from "@/context/friends-context";
import { useFriendMode } from "@/context/friend-mode-context";
import { useWeekPlan, WeekPlanProvider } from "@/context/week-plan-context";
import { Button } from "@/components/ui/button";
import { PersonAvatar } from "@/components/person-avatar";
import { WeekLayout } from "@/layouts/week-layout";
import { rememberPlannedFor } from "@/lib/compras-friends";
import type { WeekOwner } from "@/lib/week-owner";
import { switchPersonPath } from "@/lib/week-paths";
import { getCurrentWeekId, getTodayDayOfWeek } from "@/lib/week-utils";
import { cn } from "@/lib/utils";
import { useOnline } from "@/lib/use-online";

/**
 * Friend mode swaps the accent to violet (styles/globals.css) while actually
 * planning for a friend (F1, F2), not on the F3/F4 cards. Set on <html>, not
 * on a wrapper, so dialogs and menus portaled to <body> follow too.
 */
function useFriendAccent(active: boolean) {
  useLayoutEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    root.dataset.context = "friend";
    return () => {
      delete root.dataset.context;
    };
  }, [active]);
}

/** Where "Salir" / "Ir a mi plan" go: my own plan, same week and sub-page. */
function useMyPlanPath(): string {
  const { pathname } = useLocation();
  const weekId = getCurrentWeekId();
  return switchPersonPath(pathname, null, { weekId, day: getTodayDayOfWeek(weekId) ?? "mon" });
}

/**
 * `/friend/:friendId/week/:weekId/*`: planning an accepted friend's weeks
 * with the same pages as mine, under the friend's own `WeekPlanProvider`.
 *
 * States (design §6): F1 friends still loading → banner skeleton + spinner;
 * F3 not an accepted friend → card; F2 ready → banner + week; F4 a save got
 * 403 (friendship ended mid-session) → alert card, kept even after the
 * friends list refresh drops them.
 */
export function FriendWeekRoute() {
  const { friendId } = useParams<{ friendId: string }>();
  // A new friend is a new provider, and resets F4.
  const id = /^[1-9]\d*$/.test(friendId ?? "") ? Number(friendId) : Number.NaN;
  return <FriendWeekRouteFor key={friendId} friendId={id} />;
}

function FriendWeekRouteFor({ friendId }: { friendId: number }) {
  const { friends, loading, loadError, refresh } = useFriends();
  const [revokedName, setRevokedName] = useState<string | null>(null);

  const username = friends.find((f) => f.friend_id === friendId && f.status === "accepted")?.username ?? null;
  const owner = useMemo<WeekOwner | null>(
    () => (username !== null ? { kind: "friend", friendId, username } : null),
    [friendId, username],
  );

  useFriendAccent(revokedName === null && (owner !== null || loading));

  if (revokedName !== null) return <RevokedCard name={revokedName} />;
  if (!owner) return loading ? <FriendLoading failed={loadError} onRetry={() => void refresh()} /> : <NotFriendCard />;

  return (
    <WeekPlanProvider key={friendId} owner={owner}>
      <FriendWorkspace
        onRevoked={(name) => {
          setRevokedName(name);
          void refresh();
        }}
      />
    </WeekPlanProvider>
  );
}

function FriendWorkspace({ onRevoked }: { onRevoked: (name: string) => void }) {
  const { state, owner, discardPendingSaves } = useWeekPlan();
  const { setSession } = useFriendMode();
  const friendId = owner.kind === "friend" ? owner.friendId : 0;
  const unsaved = state.saveStatus === "error";
  const viewerId = useAuth().user?.id ?? null;

  // Planning for a friend includes them in my Compras, unless I turned them off there.
  useEffect(() => {
    if (viewerId !== null && friendId > 0) rememberPlannedFor(viewerId, friendId);
  }, [viewerId, friendId]);

  useEffect(() => {
    if (state.saveStatus === "forbidden") onRevoked(owner.username);
  }, [state.saveStatus, owner.username, onRevoked]);

  // Tell the navbar (above this provider) who we're planning for.
  useEffect(() => {
    setSession(() => ({ friendId, username: owner.username, unsaved, discardUnsaved: discardPendingSaves }));
  }, [setSession, friendId, owner.username, unsaved, discardPendingSaves]);
  useEffect(
    () => () => setSession((prev) => (prev?.friendId === friendId ? null : prev)),
    [setSession, friendId],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <FriendBanner />
      <WeekLayout />
    </div>
  );
}

// ── Banner ──

/**
 * The violet strip under the navbar; it never scrolls (WeekLayout scrolls
 * inside). It publishes its height as `--friend-banner-height` so toasts
 * (ComprasToast) open below it instead of over its save state.
 */
function BannerFrame({ label, children }: { label: string; children: React.ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const root = document.documentElement;
    const setVar = () => root.style.setProperty("--friend-banner-height", `${node.offsetHeight}px`);
    setVar();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(setVar);
    ro?.observe(node);
    return () => {
      ro?.disconnect();
      root.style.removeProperty("--friend-banner-height");
    };
  }, []);
  return (
    <section
      ref={ref}
      aria-label={label}
      className="relative shrink-0 border-b border-violet-200 bg-violet-50 text-violet-950 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-50"
    >
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-primary" />
      <div className="mx-auto flex max-w-3xl items-center gap-3 py-1.5 pl-4 pr-2">{children}</div>
    </section>
  );
}

function FriendBanner() {
  const { state, owner, retrySave } = useWeekPlan();
  const { leaveTo } = useFriendMode();
  const myPlanPath = useMyPlanPath();
  // A failed save needs the whole line (and Reintentar) at 375px: the plan
  // name steps aside until it's fixed.
  const planName = state.planId && state.saveStatus !== "error" ? planRegistry[state.planId].name : null;

  return (
    <BannerFrame label={`Planificando para ${owner.username}`}>
      <PersonAvatar name={owner.username} tone="current" />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm">
          Planificando para <span className="font-semibold">{owner.username}</span>
        </p>
        <p className="flex min-w-0 items-center gap-1 text-xs text-violet-800/80 dark:text-violet-200/80">
          {planName && <span className="truncate">{planName}</span>}
          <span aria-live="polite" className="flex shrink-0 items-center gap-1">
            <SaveState status={state.saveStatus} onRetry={retrySave} withSeparator={planName !== null} />
          </span>
        </p>
      </div>
      <Button
        variant="ghost"
        onClick={() => leaveTo(myPlanPath)}
        className="h-11 shrink-0 px-3 text-violet-900 hover:bg-violet-100 hover:text-violet-950 dark:text-violet-100 dark:hover:bg-violet-900/60"
      >
        Salir
      </Button>
    </BannerFrame>
  );
}

function SaveState({
  status,
  onRetry,
  withSeparator,
}: {
  status: ReturnType<typeof useWeekPlan>["state"]["saveStatus"];
  onRetry: () => void;
  withSeparator: boolean;
}) {
  const online = useOnline();
  if (status === "idle" || status === "forbidden") return null;
  const sep = withSeparator ? <span aria-hidden="true">·</span> : null;
  // Queued saves retry on their own when the connection is back.
  if (status === "error" && !online) {
    return (
      <>
        {sep}
        <span className="font-medium text-red-700 dark:text-red-300">Sin conexión · se guardará al volver</span>
      </>
    );
  }
  if (status === "saving") return <>{sep}Guardando…</>;
  if (status === "saved") {
    return (
      <>
        {sep}Guardado <CheckIcon aria-hidden="true" className="size-3.5" />
      </>
    );
  }
  return (
    <>
      {sep}
      <span className="font-medium text-red-700 dark:text-red-300">No se pudo guardar</span>
      <button
        type="button"
        onClick={onRetry}
        className="-my-2 inline-flex min-h-8 items-center gap-1 rounded-md px-1.5 font-medium text-violet-900 underline underline-offset-2 outline-none hover:bg-violet-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:text-violet-100 dark:hover:bg-violet-900/60"
      >
        <RefreshCwIcon aria-hidden="true" className="size-3" /> Reintentar
      </button>
    </>
  );
}

// ── F1 / F3 / F4 ──

/** F1, or, when the friends list couldn't load, a retry (focus retries too). */
function FriendLoading({ failed, onRetry }: { failed: boolean; onRetry: () => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-busy={!failed}>
      <BannerFrame label="Planificando para un amigo">
        <span aria-hidden="true" className="size-8 shrink-0 animate-pulse rounded-full bg-violet-200 dark:bg-violet-900" />
        <div className="min-w-0 flex-1 space-y-1.5 py-0.5">
          <p className="text-sm">Planificando para…</p>
          <span aria-hidden="true" className="block h-2.5 w-24 animate-pulse rounded bg-violet-200 dark:bg-violet-900" />
        </div>
      </BannerFrame>
      {failed ? (
        <div role="alert" className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
          <p className="text-sm text-muted-foreground">No pudimos cargar tus amigos.</p>
          <Button variant="outline" className="h-11" onClick={onRetry}>
            <RefreshCwIcon className="size-4" /> Reintentar
          </Button>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      )}
    </div>
  );
}

function StateCard({
  icon,
  title,
  body,
  alert = false,
}: {
  icon: React.ReactNode;
  title: string;
  body?: string;
  alert?: boolean;
}) {
  const navigate = useNavigate();
  const myPlanPath = useMyPlanPath();
  return (
    <div className="flex flex-1 items-center justify-center px-4 py-8">
      <div
        role={alert ? "alert" : undefined}
        className={cn(
          "w-full max-w-sm space-y-4 rounded-xl border bg-card p-5 text-center text-card-foreground shadow-sm",
          alert && "border-red-200 dark:border-red-900",
        )}
      >
        <div
          className={cn(
            "mx-auto flex size-10 items-center justify-center rounded-full",
            alert ? "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-200" : "bg-muted text-muted-foreground",
          )}
        >
          {icon}
        </div>
        <div className="space-y-1">
          <h2 className="text-base font-semibold">{title}</h2>
          {body && <p className="text-sm text-muted-foreground">{body}</p>}
        </div>
        <Button className="h-11 w-full" onClick={() => navigate(myPlanPath)}>
          Ir a mi plan
        </Button>
      </div>
    </div>
  );
}

/** F3: a friend URL for someone who isn't (or is no longer) an accepted friend. */
function NotFriendCard() {
  return (
    <StateCard
      icon={<UserXIcon className="size-5" />}
      title="No puedes planificar las comidas de este usuario"
      body="Solo puedes planificar las comidas de tus amigos."
    />
  );
}

/** F4: the friendship ended while planning; the last change didn't save. */
function RevokedCard({ name }: { name: string }) {
  return (
    <StateCard
      alert
      icon={<UserXIcon className="size-5" />}
      title={`${name} ya no es tu amigo`}
      body="Tu último cambio no se guardó."
    />
  );
}
